/**
 * IbanSection — F-002 Phase 4 (IMPL-P4-007).
 *
 * Tab "IBAN" del BillingProfileForm:
 *  - IBANInput controllato via React Hook Form `Controller`
 *  - Checkbox "Autorizzo addebito SEPA" (V1: registra un consenso semplice;
 *    F-011 estenderà con mandato firmato).
 *  - Validazione cross-field: autorizzazione SEPA spuntata SENZA IBAN →
 *    messaggio inline ammonitorio (l'invio del consenso a /api/consent-records
 *    è demandato al BillingProfileForm.onSubmit per restare coordinato col
 *    save() del profilo).
 *
 * Il valore `sepaAuthorized` è uno stato locale (non persistito sul profilo
 * via PUT — il profilo memorizza solo l'IBAN cifrato; il consenso vive in
 * ConsentRecord). Per condividere il valore con BillingProfileForm.onSubmit,
 * accetta `sepaAuthorized` + `onSepaAuthorizedChange` come props.
 */
import { Controller, useFormContext } from 'react-hook-form';
import type { BillingProfileUpsertInput } from '@app/shared';

import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { IBANInput } from './IBANInput';

export interface IbanSectionProps {
  sepaAuthorized: boolean;
  onSepaAuthorizedChange: (value: boolean) => void;
}

export function IbanSection({ sepaAuthorized, onSepaAuthorizedChange }: IbanSectionProps) {
  const { control, watch } = useFormContext<BillingProfileUpsertInput>();
  const iban = watch('iban' as never) as unknown as string | undefined;

  const sepaWithoutIban = sepaAuthorized && !iban;

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <Label htmlFor="iban">IBAN (opzionale)</Label>
        <Controller
          control={control}
          name={'iban' as const}
          render={({ field, fieldState }) => (
            <IBANInput
              id="iban"
              value={(field.value as string | undefined) ?? ''}
              onChange={field.onChange}
              error={fieldState.error?.message}
            />
          )}
        />
        <p className="text-xs text-muted-foreground">
          L'IBAN viene memorizzato cifrato. Verrà mostrato in forma mascherata (es. "IT60****WXYZ").
        </p>
      </div>

      <div className="space-y-2 rounded-md border p-4">
        <div className="flex items-start gap-3">
          <Checkbox
            id="sepa-authorized"
            checked={sepaAuthorized}
            onCheckedChange={(v) => onSepaAuthorizedChange(v === true)}
          />
          <div className="space-y-1">
            <Label htmlFor="sepa-authorized" className="text-sm font-medium">
              Autorizzo l'addebito SEPA su questo IBAN
            </Label>
            <p className="text-xs text-muted-foreground">
              Spuntando questa casella autorizzi addebiti SEPA per le fatture future. La revoca è
              sempre possibile dalla pagina del profilo.
            </p>
          </div>
        </div>

        {sepaWithoutIban ? (
          <p role="alert" className="text-sm font-medium text-destructive">
            Per autorizzare l'addebito SEPA è necessario inserire un IBAN valido.
          </p>
        ) : null}
      </div>
    </div>
  );
}
